/**
 * The supplier's live stock for one blank, the one place these words are said:
 * "{supplier}: N left", plus "back {date}" while that day has not passed.
 * Consumers: the Send order window, the Order and wait PO page and the
 * just-in-time PO page.
 *
 * Renders nothing when the line has no number (a blank not linked to the PO's
 * catalog supplier, another supplier, an older server). That test is `typeof`,
 * never a truthy check or `!== null`: 0 is a real answer and an older server
 * leaves the key out.
 *
 * The note wraps (a long supplier name never widens its row), but "back {date}"
 * is one unbroken piece: a narrow line breaks before it, never inside the date.
 */
import { useTranslation } from 'react-i18next';
import { formatDayMonth, useDateFnsLocale } from '@/lib/format-dates';
import { floorToday, upcomingDeliveryDay } from '../lib/order-and-wait';

interface SupplierStockNoteProps {
  stock: number | null | undefined;
  eta: string | null | undefined;
  /** The org's own name for the supplier, from the data. Never hard-coded. */
  supplierName: string | null | undefined;
}

export function SupplierStockNote({ stock, eta, supplierName }: SupplierStockNoteProps) {
  const { t } = useTranslation('orders');
  const locale = useDateFnsLocale();
  if (typeof stock !== 'number') return null;

  const supplier = supplierName?.trim() || t('oaw.yourSupplier');
  const backDay = upcomingDeliveryDay(eta, floorToday(undefined));

  return (
    <span className="block min-w-0 break-words text-[11px] text-muted-foreground">
      {t('oaw.send.liveStock', { supplier, count: stock })}
      {backDay && (
        <>
          {' · '}
          <span className="whitespace-nowrap">
            {t('oaw.send.liveStockBack', { date: formatDayMonth(backDay, locale) })}
          </span>
        </>
      )}
    </span>
  );
}
